/**
 * BrandWheelPreview — the live palette preview for the Brand Palette Wizard.
 *
 * Renders the CANONICAL brand-Wheel geometry (concept notes6 #3 / design law):
 *   · Portrait/inner radius = 0.75 × outer radius (the app's Portrait ratio)
 *   · PARALLEL-EDGED gaps — constant perpendicular width base→periphery
 *   · UNEQUAL segment spans (never four perfect quarters)
 * colored by the user's palette. Ported to React from the deck's canonical
 * `harness/elevations/brand-wheel.mjs` so the app preview matches the design
 * exactly (the old equal-slice donut is deliberately gone).
 *
 * Pure/presentational: give it an array of hex strings.
 */
import React from "react";
import { spansForCount } from "./logic";

const C = 60;
const R = 56;
const RATIO = 0.75;
const RIN = R * RATIO;
const GAP = 4;
const HG = GAP / 2;
const AOUT = Math.asin(HG / R);
const AIN = Math.asin(HG / RIN);

const pt = (th, r) =>
  `${(C + r * Math.sin(th)).toFixed(3)} ${(C - r * Math.cos(th)).toFixed(3)}`;

function segmentPath(startDeg, spanDeg) {
  const b0 = (startDeg * Math.PI) / 180;
  const b1 = ((startDeg + spanDeg) * Math.PI) / 180;
  const laf = b1 - b0 > Math.PI ? 1 : 0;
  return (
    `M${pt(b0 + AOUT, R)}` +
    ` A${R} ${R} 0 ${laf} 1 ${pt(b1 - AOUT, R)}` +
    ` L${pt(b1 - AIN, RIN)}` +
    ` A${RIN} ${RIN} 0 ${laf} 0 ${pt(b0 + AIN, RIN)} Z`
  );
}

export default function BrandWheelPreview({ colors = [], size = 168 }) {
  const hexes = (Array.isArray(colors) ? colors : []).filter(Boolean);
  const n = Math.max(hexes.length, 1);
  const spans = spansForCount(n);

  let start = 0;
  const paths = spans.map((span, i) => {
    const d = segmentPath(start, span);
    start += span;
    return <path key={i} d={d} fill={hexes[i % hexes.length] || "#E1E1E1"} />;
  });

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      role="img"
      aria-label="Brand palette previewed on a Wheel"
      style={{ display: "block", flex: "0 0 auto" }}
    >
      {paths}
      {/* white Portrait disc */}
      <circle cx={C} cy={C} r={(RIN - 1.5).toFixed(1)} fill="#fff" />
    </svg>
  );
}
